import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";

import { sortBy } from "lodash";
import { Panel, PanelBody, PanelHeader } from "../../components/Panel";
import { Box } from "../../components/Box";
import { Text } from "../../components/Text";
import { CheckBox } from "../../components/Checkbox";
import Loader from "../../components/Loader";
import { Flex } from "../../components/Flex";
import Button from "../../components/Button";
import { TagTime } from "@aika/types";

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  & th {
    font-weight: 600;
    border-bottom: 1px solid ${(props) => props.theme.colors.grey2};
    text-transform: uppercase;
    font-size: 12px;
    color: ${(props) => props.theme.colors.grey4};
    padding: 6px 12px;
    padding-top: 12px;
    text-align: right;

    &:first-child {
      text-align: left;
    }
  }

  & td {
    padding: 6px 12px;
    text-align: right;
    font-size: 14px;

    &:first-child {
      text-align: left;
    }
  }
`;

const formatMinutes = (minutes: number) =>
  `${Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0")}:${(minutes % 60).toString().padStart(2, "0")}`;

const TimeByTagWidget = () => {
  const [data, setData] = useState<TagTime[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (data == null) {
      axios.get<TagTime[]>("/api/reports/time-by-tag").then(({ data }) => {
        setData(sortBy(data, (x) => x.past_time).reverse());
        setLoading(false);
      });
    }
  }, [data]);

  const selectTag = (id: string) => (val: boolean) => {
    if (val) {
      setSelected((old) => [...old, id]);
    } else {
      setSelected((old) => old.filter((x) => x !== id));
    }
  };

  const deleteSelected = () => {
    if (
      window.confirm("Are you sure you want to delete all the selected tags?")
    ) {
      const ids = [...selected];
      ids.forEach((x) => axios.delete(`/api/tags/${x}`));
      setSelected([]);
      setData((old) => old.filter((x) => !ids.includes(x.id)));
    }
  };

  const mergeSelected = () => {
    const newName = window.prompt(
      "What name would you like to use for the merged/renamed tag?"
    );

    if (newName) {
      setLoading(true);
      axios.put("/api/tags/merge", { newName, tags: selected }).then(() => {
        setSelected([]);
        setData(null);
      });
    }
  };

  return (
    <Panel p={0} gridColumn={["auto", "auto", "auto", "span 2"]}>
      <PanelHeader>
        <Flex
          justifyContent="space-between"
          alignItems={["flex-start", "center"]}
          flexDirection={["column", "row"]}
        >
          <Text fontSize={[3, 4]} fontWeight={600} mb={[1, 0]}>
            Time By Tag (All Time)
          </Text>
          <Flex alignItems="center">
            <Button
              color="warning"
              mr={1}
              disabled={selected.length === 0}
              onClick={mergeSelected}
              small
            >
              Merge/Rename
            </Button>
            <Button
              color="danger"
              disabled={selected.length === 0}
              small
              onClick={deleteSelected}
            >
              Delete
            </Button>
          </Flex>
        </Flex>
      </PanelHeader>
      <PanelBody style={{ overflowY: "auto" }}>
        {loading || data == null ? (
          <Loader />
        ) : (
          <Box style={{ overflowX: "auto" }}>
            <Table>
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Block Count</th>
                  <th>Total Time</th>
                  <th>Time (Past)</th>
                  <th>Time (Future)</th>
                </tr>
              </thead>
              <tbody>
                {data.map((x) => (
                  <tr key={x.id}>
                    <td>
                      <Flex alignItems="center">
                        <CheckBox
                          mb={0}
                          mr={2}
                          value={selected.includes(x.id)}
                          onChange={selectTag(x.id)}
                          label={`#${x.name}`}
                        />
                      </Flex>
                    </td>
                    <td>{x.block_count}</td>
                    <td>{formatMinutes(x.past_time + x.future_time)}</td>
                    <td>{formatMinutes(x.past_time)}</td>
                    <td>{formatMinutes(x.future_time)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </PanelBody>
    </Panel>
  );
};

export default TimeByTagWidget;
